import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import AddressAutocomplete from '../../../components/AddressAutocomplete'
import DepthTierSelect from '../../../components/DepthTierSelect'
import {
  getAllDiveSites,
  createDiveSite,
  updateDiveSite,
  deleteDiveSite,
  uploadDiveSiteImage
} from '../../../services/diveSiteService'
import Database from '../../../types/Database'

type DiveSiteRow = Database.DiveSite & {
  locationDisplay: string
}

interface FormState {
  name: string
  description: string
  address: { lat: number; lng: number; displayName: string } | null
  pictureFile: File | null
  pictureUrl: string
  depthTier: '18m' | '30m' | '30m+' | ''
}

const emptyForm: FormState = {
  name: '',
  description: '',
  address: null,
  pictureFile: null,
  pictureUrl: '',
  depthTier: ''
}

/** Parse CSV text handling quoted fields (commas inside quotes). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        field += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === ',') {
        row.push(field)
        field = ''
      } else if (ch === '\n' || (ch === '\r' && text[i + 1] === '\n')) {
        row.push(field)
        field = ''
        if (row.some(c => c.trim())) rows.push(row)
        row = []
        if (ch === '\r') i++
      } else {
        field += ch
      }
    }
  }
  // last row
  row.push(field)
  if (row.some(c => c.trim())) rows.push(row)
  return rows
}

const DiveSitesTab = () => {
  const [sites, setSites] = useState<DiveSiteRow[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning'
  }>({ open: false, message: '', severity: 'success' })
  const [csvUploading, setCsvUploading] = useState(false)

  const fetchSites = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAllDiveSites()
      setSites(
        data.map(s => ({
          ...s,
          locationDisplay: s.location
            ? `${s.location.latitude.toFixed(
                4
              )}, ${s.location.longitude.toFixed(4)}`
            : '—'
        }))
      )
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to load dive sites: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSites()
  }, [fetchSites])

  const openAddDialog = () => {
    setEditingId(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setCsvUploading(true)
    try {
      const text = await file.text()
      const rows = parseCsv(text)
      if (rows.length < 2)
        throw new Error('CSV must have a header row and at least one data row')

      const header = rows[0].map(h => h.trim().toLowerCase())
      const nameIdx = header.indexOf('name')
      const addressIdx = header.indexOf('address')
      const latIdx = header.indexOf('lat')
      const lngIdx = header.indexOf('lng')
      const descIdx = header.indexOf('description')
      const imgIdx = header.indexOf('pictureurl')
      const depthIdx = header.indexOf('depthtier')

      if (
        nameIdx === -1 ||
        addressIdx === -1 ||
        latIdx === -1 ||
        lngIdx === -1 ||
        descIdx === -1
      ) {
        throw new Error(
          'CSV must have columns: name, address, lat, lng, description. Optional: pictureUrl, depthTier'
        )
      }

      let created = 0
      const errors: string[] = []

      for (let i = 1; i < rows.length; i++) {
        const cols = rows[i]
        const name = cols[nameIdx]?.trim()
        const address = cols[addressIdx]?.trim()
        const latRaw = cols[latIdx]?.trim()
        const lngRaw = cols[lngIdx]?.trim()
        const description = cols[descIdx]?.trim()
        const pictureUrl = imgIdx !== -1 ? cols[imgIdx]?.trim() || null : null
        const depthRaw = depthIdx !== -1 ? cols[depthIdx]?.trim() : ''
        const depthTier: '18m' | '30m' | '30m+' =
          depthRaw === '30m+' || depthRaw === '30m' ? depthRaw : '18m'

        const missing: string[] = []
        if (!name) missing.push('name')
        if (!address) missing.push('address')
        if (!description) missing.push('description')
        if (!latRaw || isNaN(parseFloat(latRaw))) missing.push('lat')
        if (!lngRaw || isNaN(parseFloat(lngRaw))) missing.push('lng')

        if (missing.length) {
          errors.push(`Row ${i + 1}: missing or invalid ${missing.join(', ')}`)
          continue
        }

        try {
          await createDiveSite({
            name,
            address,
            location: { lat: parseFloat(latRaw), lng: parseFloat(lngRaw) },
            pictureUrl,
            description,
            depthTier
          })
          created++
        } catch (err) {
          errors.push(
            `Row ${i + 1}: ${
              err instanceof Error ? err.message : 'failed to save'
            }`
          )
        }
      }

      const msg = errors.length
        ? `Imported ${created} site${created !== 1 ? 's' : ''}. ${
            errors.length
          } row(s) failed:\n${errors.slice(0, 5).join('\n')}${
            errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''
          }`
        : `Successfully imported ${created} site${created !== 1 ? 's' : ''}.`
      setSnackbar({
        open: true,
        message: msg,
        severity:
          errors.length && !created
            ? 'error'
            : errors.length
            ? 'warning'
            : 'success'
      })
      if (created) await fetchSites()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `CSV import failed: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setCsvUploading(false)
    }
  }

  const openEditDialog = (site: DiveSiteRow) => {
    setEditingId(site.id)
    setForm({
      name: site.name,
      description: site.description,
      address: site.location
        ? {
            lat: site.location.latitude,
            lng: site.location.longitude,
            displayName: site.address
          }
        : null,
      pictureFile: null,
      pictureUrl: site.pictureUrl || '',
      depthTier: site.depthTier || ''
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!form.name.trim() || !form.address || !form.description.trim()) return

    setSaving(true)
    try {
      let pictureUrl = form.pictureUrl
      if (form.pictureFile) {
        pictureUrl = await uploadDiveSiteImage(form.pictureFile)
      }

      const payload = {
        name: form.name.trim(),
        address: form.address.displayName,
        location: { lat: form.address.lat, lng: form.address.lng },
        pictureUrl: pictureUrl || null,
        description: form.description.trim(),
        depthTier: form.depthTier || null
      }

      if (editingId) {
        await updateDiveSite(editingId, payload)
        setSnackbar({
          open: true,
          message: 'Dive site updated.',
          severity: 'success'
        })
      } else {
        await createDiveSite(payload)
        setSnackbar({
          open: true,
          message: 'Dive site created.',
          severity: 'success'
        })
      }

      setDialogOpen(false)
      setForm(emptyForm)
      setEditingId(null)
      await fetchSites()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to save: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirmId) return
    setDeleting(true)
    try {
      await deleteDiveSite(deleteConfirmId)
      setSnackbar({
        open: true,
        message: 'Dive site deleted.',
        severity: 'success'
      })
      setDeleteConfirmId(null)
      await fetchSites()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to delete: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setDeleting(false)
    }
  }

  const formValid =
    form.name.trim().length > 0 &&
    form.description.trim().length > 0 &&
    form.address !== null

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant='h6'>Dive Sites</Typography>
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            component='label'
            startIcon={
              csvUploading ? <CircularProgress size={16} /> : <UploadFileIcon />
            }
            disabled={csvUploading}
          >
            {csvUploading ? 'Importing...' : 'Import CSV'}
            <input
              type='file'
              hidden
              accept='.csv'
              onChange={handleCsvUpload}
            />
          </Button>
          <Button variant='contained' onClick={openAddDialog}>
            Add Dive Site
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Depth</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  <Typography color='text.secondary'>
                    No dive sites yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sites.map(site => (
                <TableRow key={site.id}>
                  <TableCell>{site.name}</TableCell>
                  <TableCell>{site.address}</TableCell>
                  <TableCell>{site.depthTier ?? '—'}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 250,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {site.description}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size='small'
                      onClick={() => openEditDialog(site)}
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      color='error'
                      onClick={() => setDeleteConfirmId(site.id)}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editingId ? 'Edit Dive Site' : 'Add Dive Site'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Name'
              fullWidth
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              inputProps={{ maxLength: 100 }}
            />

            <AddressAutocomplete
              value={form.address}
              onChange={val => setForm(f => ({ ...f, address: val }))}
            />

            <TextField
              label='Description'
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={e =>
                setForm(f => ({ ...f, description: e.target.value }))
              }
              inputProps={{ maxLength: 500 }}
            />

            <DepthTierSelect
              value={form.depthTier}
              onChange={val => setForm(f => ({ ...f, depthTier: val }))}
            />

            <Box>
              <Button variant='outlined' component='label'>
                {form.pictureFile
                  ? form.pictureFile.name
                  : form.pictureUrl
                  ? 'Change Picture'
                  : 'Upload Picture'}
                <input
                  type='file'
                  hidden
                  accept='image/jpeg,image/png,image/webp'
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) setForm(f => ({ ...f, pictureFile: file }))
                  }}
                />
              </Button>
              {form.pictureUrl && !form.pictureFile && (
                <Typography variant='caption' sx={{ ml: 1 }}>
                  Existing image will be kept
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={saving || !formValid}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmId}
        onClose={() => !deleting && setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Dive Site</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this dive site? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'warning' ? 12000 : 5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
          sx={{ whiteSpace: 'pre-line' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DiveSitesTab
